// Staff access (backend 1.0.0.758+, docs/staff-access.md): the panel has no administrator role any more. Staff open
// it from the CRM's «پنل» button and work in the sections their CRM role grants, at «مشاهده» or «مدیریت»; a CRM full
// administrator (or an on-premises build's administrator) has every section. This module imports nothing: AuthHelpers
// and the helpers' layout read it while _metronic/helpers is still initialising.

export type StaffLevel = 'view' | 'manage'

/** The sections, keyed as the CRM table, the signed entry and the session name them. Never rename a key. */
export const STAFF_SECTIONS = {
  businesses: 'کسب‌وکارها',
  invoices: 'صورتحساب‌ها',
  reports: 'گزارش‌ها',
  logs: 'لاگ‌ها',
  users: 'کاربران سامانه',
  partners: 'همکاران API',
  monitoring: 'مانیتورینگ و ارسال',
  sms: 'پیامک',
  callCenter: 'مرکز تماس',
  services: 'خدمات و قیمت‌گذاری',
  payments: 'کیف پول و پرداخت آنلاین',
  accountingSync: 'اتصال‌های حسابداری',
  catalog: 'شناسه کالا/خدمت و قیمت‌های آنلاین',
  automaticInvoice: 'صدور خودکار',
  taxWorkspace: 'کارپوشه مالیاتی',
  maintenance: 'ابزارهای نگهداری',
  supplyChain: 'زنجیره تأمین',
} as const

export type StaffSection = keyof typeof STAFF_SECTIONS

export interface StaffSessionModel {
  sessionId: string
  crmUserId: number
  name: string
  admin: boolean
  access: Partial<Record<StaffSection, StaffLevel>>
  expiresAt: string
}

type WithStaff = {staff?: StaffSessionModel | null} | null | undefined

/** A session opened from the CRM's «پنل» button (or an on-premises administrator's). */
export const isStaff = (auth: WithStaff): boolean => !!auth?.staff

/** The level the session holds on a section, or undefined for none. */
export const staffLevel = (auth: WithStaff, section: StaffSection): StaffLevel | undefined => {
  const staff = auth?.staff
  if (!staff) return undefined
  if (staff.admin) return 'manage'
  const level = staff.access?.[section]
  return level === 'manage' || level === 'view' ? level : undefined
}

/** «مدیریت» includes «مشاهده». */
export const staffCan = (
  auth: WithStaff,
  section: StaffSection,
  level: StaffLevel = 'view'
): boolean => {
  const held = staffLevel(auth, section)
  if (!held) return false
  return level === 'view' || held === 'manage'
}

/**
 * Staff who pick any business (and «همه کسب‌وکارها») on the pages with the business picker: «کسب‌وکارها» at
 * «مدیریت», because entering a business makes the session its full member there (the server holds no member to a
 * view-only role on most changes; security review 2026-10-30). At «مشاهده» staff read the lists and any business's
 * settings, and look inside a business through the CRM's read-only support sign-in. The selection narrows only the
 * pages with the picker (admin-business-scope rule, 2026-10-05).
 */
export const staffPicksBusiness = (auth: WithStaff): boolean => staffCan(auth, 'businesses', 'manage')

/** Such staff with no business selected yet (cpi 0): per-business lists wait for one. */
export const staffWithoutBusiness = (
  auth: (WithStaff & {cpi?: number}) | null | undefined
): boolean => staffPicksBusiness(auth) && Number(auth?.cpi || 0) <= 0

/** Any section at all: the session is staff with something to do. */
export const staffHasAnySection = (auth: WithStaff): boolean =>
  (Object.keys(STAFF_SECTIONS) as StaffSection[]).some((section) => staffCan(auth, section))

/** The session's end, for the header badge. */
export const staffExpiresAt = (auth: WithStaff): Date | undefined => {
  const at = auth?.staff?.expiresAt
  if (!at) return undefined
  const date = new Date(at)
  return Number.isNaN(date.getTime()) ? undefined : date
}
